import { Timestamp } from 'firebase/firestore';
import type { PersistentRoom, RoomMember, RoomRole } from '../../../types/room';

/**
 * Generates a unique room ID.
 */
export function generateRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 12; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

/**
 * Converts a Firestore member to a RoomMember.
 */
export function convertMember(data: Record<string, unknown>): RoomMember {
  return {
    userId: data.userId as string,
    email: data.email as string,
    displayName: (data.displayName as string) || '',
    role: data.role as RoomRole,
    joinedAt:
      data.joinedAt instanceof Timestamp
        ? data.joinedAt.toDate()
        : new Date(data.joinedAt as string),
  };
}

/**
 * Checks if a user is an admin of a room.
 */
export function isRoomAdmin(
  room: PersistentRoom,
  userId: string,
  email?: string
): boolean {
  if (room.adminUserIds?.includes(userId)) {
    return true;
  }

  if (email) {
    const normalizedEmail = email.toLowerCase();
    const hasAdminEmail = room.adminUserIds?.some(
      (adminId) => typeof adminId === 'string' && adminId.toLowerCase() === normalizedEmail
    );
    if (hasAdminEmail) {
      return true;
    }
  }

  const member = room.members.find(
    (m) => m.userId === userId || (email && m.email === email)
  );
  return member?.role === 'admin';
}

/**
 * Checks if a user is a member of a room by userId or email.
 */
export function isRoomMember(
  room: PersistentRoom,
  userId: string,
  email?: string
): boolean {
  return room.members.some(
    (m) => m.userId === userId || (email && m.email === email)
  );
}
